import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Drawer, Empty, List, notification, Popconfirm, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, EditOutlined, PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'

import {
	createHousingAssociation,
	deleteHousingAssociation,
	getHousingAssociations,
	getHousingAssociationPriceOffers,
	updateHousingAssociation,
	IHousingAssociation,
	IHousingAssociationPriceOffer
} from '../../reducers/housingAssociations/actions'
import HousingAssociationModal from './HousingAssociationModal'
import { history } from '../../utils/history'
import { PRICE_OFFER_STATUS, translatePriceOfferStatusColor, translatePriceOfferStatusToSk } from '../../utils/enums'

type DrawerItemProps = {
	order: IHousingAssociationPriceOffer
	priceOfferDetailPath: string
}

function DrawerOrderItem({ order, priceOfferDetailPath }: DrawerItemProps) {
	return (
		<List.Item
			style={{ cursor: 'pointer' }}
			onClick={() => history.push(`${priceOfferDetailPath}/${order.id}`)}
		>
			<List.Item.Meta
				title={<span style={{ fontWeight: 500 }}>{ order.address || `#${order.id}` }</span>}
				description={
					<Tag color={translatePriceOfferStatusColor(order.status as PRICE_OFFER_STATUS)}>
						{ translatePriceOfferStatusToSk(order.status as PRICE_OFFER_STATUS) }
					</Tag>
				}
			/>
		</List.Item>
	)
}

const HousingAssociations = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation('pages')

	const [associations, setAssociations] = useState<IHousingAssociation[]>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [submitting, setSubmitting] = useState<boolean>(false)
	const [modalVisible, setModalVisible] = useState<boolean>(false)
	const [editing, setEditing] = useState<IHousingAssociation | null>(null)

	const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
	const [drawerAssociation, setDrawerAssociation] = useState<IHousingAssociation | null>(null)
	const [drawerOrders, setDrawerOrders] = useState<IHousingAssociationPriceOffer[]>([])
	const [drawerLoading, setDrawerLoading] = useState<boolean>(false)

	const loadData = useCallback(() => {
		setLoading(true)
		dispatch(getHousingAssociations(
			(data) => {
				setAssociations(Array.isArray(data) ? data : [])
				setLoading(false)
			},
			() => setLoading(false)
		))
	}, [dispatch])

	useEffect(() => {
		loadData()
	}, [loadData])

	const openCreate = () => {
		setEditing(null)
		setModalVisible(true)
	}

	const openEdit = (record: IHousingAssociation) => {
		setEditing(record)
		setModalVisible(true)
	}

	const openDrawer = (record: IHousingAssociation) => {
		setDrawerAssociation(record)
		setDrawerOrders([])
		setDrawerOpen(true)
		setDrawerLoading(true)
		dispatch(getHousingAssociationPriceOffers(
			record.id,
			(data) => {
				setDrawerOrders(Array.isArray(data) ? data : [])
				setDrawerLoading(false)
			},
			() => setDrawerLoading(false)
		))
	}

	const handleSubmit = (name: string) => {
		setSubmitting(true)
		const onSuccess = (message: string) => {
			setSubmitting(false)
			setModalVisible(false)
			setEditing(null)
			notification.success({ message })
			loadData()
		}
		const onFailure = () => setSubmitting(false)

		if (editing) {
			dispatch(updateHousingAssociation(editing.id, name, () => onSuccess(t('pages:Bytové družstvo bolo upravené')), onFailure))
		} else {
			dispatch(createHousingAssociation(name, () => onSuccess(t('pages:Bytové družstvo bolo vytvorené')), onFailure))
		}
	}

	const handleDelete = (record: IHousingAssociation) => {
		dispatch(deleteHousingAssociation(record.id, () => {
			notification.success({ message: t('pages:Bytové družstvo bolo zmazané') })
			loadData()
		}))
	}

	const renderDocument = (label?: string | null, fileName?: string | null) => {
		if (!fileName) {
			return <span style={{ color: 'rgba(0, 0, 0, 0.35)' }}>—</span>
		}
		return (
			<div>
				{ label && <div className={'font-semibold'}>{ label }</div> }
				<div style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 12 }}>{ fileName }</div>
			</div>
		)
	}

	const renderOrdersCount = (record: IHousingAssociation) => (
		<Tag color={record.activeOrdersCount > 0 ? 'blue' : 'default'}>
			{ record.activeOrdersCount }
		</Tag>
	)

	const renderActions = (record: IHousingAssociation) => {
		if (record.id === 0) return null
		return (
			<div className={'flex justify-end'} style={{ gap: 8 }}>
				<Button
					icon={<EditOutlined />}
					size={'small'}
					onClick={(e) => { e.stopPropagation(); openEdit(record) }}
				>
					{ t('pages:Upraviť') }
				</Button>
				<Popconfirm
					title={t('pages:Naozaj chcete zmazať toto bytové družstvo?')}
					icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
					okText={t('pages:Áno')}
					cancelText={t('pages:Nie')}
					onConfirm={(e) => { e?.stopPropagation(); handleDelete(record) }}
				>
					<Button icon={<DeleteOutlined />} size={'small'} danger onClick={(e) => e.stopPropagation()} />
				</Popconfirm>
			</div>
		)
	}

	const columns: ColumnsType<IHousingAssociation> = [
		{
			title: t('pages:Názov bytového družstva'),
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: t('pages:Príloha 1 (inflačná doložka)'),
			key: 'document1',
			render: (_, record) => renderDocument(record.document1Label, record.document1FileName)
		},
		{
			title: t('pages:Príloha 2 (inflačná doložka)'),
			key: 'document2',
			render: (_, record) => renderDocument(record.document2Label, record.document2FileName)
		},
		{
			title: t('pages:Zákazky'),
			key: 'activeOrdersCount',
			width: 100,
			align: 'center',
			render: (_, record) => renderOrdersCount(record)
		},
		{
			title: '',
			key: 'actions',
			width: 160,
			align: 'right',
			render: (_, record) => renderActions(record)
		}
	]

	return (
		<div className={'page-wrapper'}>
			<div className={'flex justify-between items-center mb-4'}>
				<h1 style={{ margin: 0 }}>{ t('pages:Bytové družstvá') }</h1>
				<Button
					icon={<PlusCircleOutlined />}
					type={'primary'}
					onClick={openCreate}
				>
					{ t('pages:Vytvoriť bytové družstvo') }
				</Button>
			</div>
			<Table
				className={'general-table'}
				rowKey={'id'}
				columns={columns}
				dataSource={associations}
				loading={loading}
				pagination={false}
				locale={{ emptyText: <Empty description={t('pages:Žiadne dáta')} /> }}
				size={'small'}
				onRow={(record) => ({
					onClick: () => openDrawer(record),
					style: {
						cursor: 'pointer',
						...(record.id === 0 ? { color: 'rgba(0,0,0,0.4)', fontStyle: 'italic' } : {})
					}
				})}
			/>
			<HousingAssociationModal
				visible={modalVisible}
				initialName={editing?.name || ''}
				title={editing ? t('pages:Upraviť bytové družstvo') : t('pages:Vytvoriť bytové družstvo')}
				confirmLoading={submitting}
				onCancel={() => { setModalVisible(false); setEditing(null) }}
				onSubmit={handleSubmit}
			/>
			<Drawer
				title={drawerAssociation?.name ?? t('pages:Bez Bytového Združenia')}
				visible={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				width={480}
			>
				<List
					loading={drawerLoading}
					dataSource={drawerOrders}
					locale={{ emptyText: <Empty description={t('pages:Žiadne zákazky')} /> }}
					renderItem={(order) => (
						<DrawerOrderItem
							order={order}
							priceOfferDetailPath={t('paths:priceOfferDetail|path')}
						/>
					)}
				/>
			</Drawer>
		</div>
	)
}

export default HousingAssociations
